import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DeckGL, { MapController } from "deck.gl";
import { MapView, WebMercatorViewport } from "@deck.gl/core";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  PieChart,
  Pie,
  Cell,
  Label as RechartsLabel
} from "recharts";

import { renderAllLayers } from "./render_layers";

import Legend from "./legend.json";

const Years = [1976, 1987, 1991, 1997, 2006, 2009, 2014, 2016, 2021];
const LanduseType = ["forest", "water", "paddy", "farmland", "building", "barren"];

const JapanBounds = [
  [122.89305980289718, 23.977964821892858],
  [145.9483624174012, 45.64257950403767]
];

const App = () => {
  // 地図画面の大きさ
  const mapSize = () => {
    const sidebar_width = 360;   // --sidebar-width
    const topbar_height = 68;    // --topbar-height

    return {
      width: window.innerWidth - sidebar_width,
      height: window.innerHeight - topbar_height
    };
  };

  // 日本全国fitzoneするときのViewState
  const fitViewState = () => {
    const {width, height} = mapSize();

    const viewport = new WebMercatorViewport({
      width,
      height,
    });
    const {longitude, latitude, zoom} = viewport.fitBounds(JapanBounds);

    return {
      width,
      height,
      longitude: longitude,
      latitude: latitude,
      zoom: zoom,
      minZoom: 3,
      maxZoom: 17,
      pitch: 40,
    };
  };

  // InitViewState
  const viewstate_init = fitViewState();
  const [viewState, setViewState] = useState(viewstate_init);

  // mapの繰り返し
  const views = useMemo(() => new MapView({ repeat: true }), []);

  // map移動時
  const onViewStateChange = ({viewState}) => {
    setViewState(viewState);
  };

  // ウィンドウリサイズ
  const handleResize = () => {
    const {width, height} = mapSize();

    setViewState((prev) => ({
      ...prev,
      width,
      height
    }));
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // fitzoneHandler
  const fitzoneHandler = () => {
    setViewState(fitViewState());
  };

  // landusetype
  const [landuseType, setLanduseType] = useState("major");
  const onChangeLanduseType = ({target}) => {
    setLanduseType(target.value);
  };

  // year
  const [yearIndex, setYearIndex] = useState(Years.length - 1);
  const [yearIsPlaying, setYearIsPlaying] = useState(false);

  // yearを自動再生
  useEffect(() => {
    if (!yearIsPlaying) return;

    setYearIndex(0);

    const id = window.setInterval(() => {
      setYearIndex((i) => (i + 1) % Years.length);
    }, 2000);

    return () => window.clearInterval(id);
  }, [yearIsPlaying]);

  // grid_type
  const [gridType, setGridType] = useState("h3");

  // LanduseCellAreaData
  const [landuseCellAreaData, setLanduseCellAreaData] = useState(null);
  useEffect(() => {
    const year = Years[yearIndex];
    const url = `https://f52hycgcyzhfcpshvocghydypu0iuped.lambda-url.ap-northeast-1.on.aws/landuse_cell_area?year=${year}&landuse_type=${landuseType}&grid_type=${gridType}`;

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return null;
        } else {
          return res.json();
        }
      })
      .then(json => {
        setLanduseCellAreaData(json);
      })
      .catch(() => {
        setLanduseCellAreaData(null);
      });
  }, [landuseType, yearIndex, gridType]);

  // layer
  const layers = useMemo(() => {
    return renderAllLayers({landuseType: landuseType, gridType: gridType, mapData: landuseCellAreaData});
  }, [landuseCellAreaData]);

  // cursor
  const [cursor, setCursor] = useState("grab");
  const hoverMapHandler = ({ object, layer }) => {
    if (layer && (layer.id === "data-layer")) {
      setCursor("pointer");
    } else {
      setCursor("grab");
    }
  };

  // selectedCell
  const [selectedCell, setSelectedCell] = useState(null);
  const clickMapHandler = ({object}) => {
    if (!object) return;

    setSelectedCell(object.cell);
  };

  // CellLanduseAreaData
  const [cellLanduseAreaData, setCellLanduseAreaData] = useState(
    Object.fromEntries(
      Years.map((year) => [year, []])
    )
  );
  const [isReadyCellLanduseAreaData, setIsReadyCellLanduseAreaData] = useState(false);

  useEffect(() => {
    if (!selectedCell) {
      setCellLanduseAreaData(
        Object.fromEntries(
          Years.map((year) => [year, []])
        )
      );

      return;
    }

    let alive = true;
    setIsReadyCellLanduseAreaData(false);

    const data = Years.map(async (year) => {
      const url = `https://f52hycgcyzhfcpshvocghydypu0iuped.lambda-url.ap-northeast-1.on.aws/cell_landuse_area?year=${year}&grid_type=${gridType}&cell=${selectedCell}`;

      try {
        const res = await fetch(url);
        if (!res.ok) return [year, []];

        const json = await res.json();

        return [year, json ?? []];
      } catch {
        return [year, []];
      }
    });

    Promise.all(data).then(entries => {
      if (!alive) return;

      setCellLanduseAreaData(Object.fromEntries(entries));
      setIsReadyCellLanduseAreaData(true);
    });

    return () => { alive = false; };
  }, [gridType, selectedCell]);

  // areaChartData, areaChartKeyData
  const [areaChartData, areaChartKeyData] = useMemo(() => {
    if (!selectedCell || !isReadyCellLanduseAreaData) return [[], []];

    const areaChartData = Years.map((yearKey) => {
      const yearData = Object.fromEntries(
        cellLanduseAreaData[yearKey].map((element) => [element.landuse, element.area_ratio])
      );

      const landuseCellAreaRatioData = Object.fromEntries(
        LanduseType.map((landuseKey) => [landuseKey, yearData[landuseKey] ?? 0])
      );

      return {
        year: yearKey,
        ...landuseCellAreaRatioData
      };
    });

    const areaChartKeyData = LanduseType.map((landuseKey) => [
      landuseKey,
      areaChartData.reduce((x1, x2) => x1 + (x2[landuseKey] ?? 0), 0)
    ]).filter(
      (element) => element[1] > 0
    ).sort(
      (x, y) => y[1] - x[1]
    ).map(
      (element) => element[0]
    );

    return [
      areaChartData,
      areaChartKeyData
    ];
  }, [cellLanduseAreaData, isReadyCellLanduseAreaData]);

  // pieYear
  const [pieYear, setPieYear] = useState(String(Years[Years.length - 1]));
  const onChangePieYear = ({target}) => {
    setPieYear(target.value);
  };

  // pieData
  const pieData = useMemo(() => {
    if (!selectedCell || !isReadyCellLanduseAreaData) return [];

    return cellLanduseAreaData[pieYear]?? [];
  }, [cellLanduseAreaData, isReadyCellLanduseAreaData, pieYear]);

  const rgbToCss = ([r, g, b, a]) => `rgb(${r}, ${g}, ${b})`;

  const clearChartHandler = () => {
    setSelectedCell(null);
    setPieYear(String(Years[Years.length - 1]));
  };

  return (
    <div className="App">
      <div className="map-container">
        <DeckGL
          layers={layers}
          initialViewState={viewstate_init}
          viewState={viewState}
          views={views}
          controller={{ type: MapController, dragRotate: false }}
          onViewStateChange={onViewStateChange}
          getCursor={() => cursor}
          onHover={hoverMapHandler}
          onClick={clickMapHandler}
          /* getTooltip={getTooltip} */
        />
      </div>

      <div className="top-panel panel">
        <div className="topbar">

          {/* topbar-controls start */}
          <div className="topbar-controls">
            {/* map type start */}
            <div className="topbar-group">
              <span className="label">Land Use</span>
              <select
                className="map-type-select"
                value={landuseType}
                onChange={onChangeLanduseType}
              >
                <option value="major" key="major">Major</option>
                {
                  LanduseType.map((key) => {
                    const item = Legend[key];

                    return (
                      <option value={key} key={key}>
                        {item.label}
                      </option>
                    );
                  })
                }
              </select>
            </div>
            {/* map type end */}

            {/* grid_type start */}
            <div className="topbar-group">
              <span className="label">Grid</span>

              <label>
                <input
                  type="radio"
                  name="grid_type"
                  value="h3"
                  checked={gridType === "h3"}
                  onChange={(e) => setGridType(e.target.value)}
                />
                H3
              </label>

              <label>
                <input
                  type="radio"
                  name="grid_type"
                  value="a5"
                  checked={gridType === "a5"}
                  onChange={(e) => setGridType(e.target.value)}
                />
                A5
              </label>
            </div>
            {/* grid_type end */}
          </div>
          {/* topbar-controls end */}

          {/* topbar-timeline start */}
          <div className="topbar-timeline">
            <div className="topbar-group year-group">

              <span className="label">Year</span>

              <div className="year-slider-wrapper">
                {/* slider */}
                <input
                  type="range"
                  className="year-slider"
                  min="0"
                  max={Years.length - 1}
                  step="1"
                  value={yearIndex}
                  onChange={(e) => setYearIndex(Number(e.target.value))}
                />

                {/* labels */}
                <div className="year-labels">
                  {
                    Years.map((y, i) => (
                      <span
                        key={y}
                        className={`year-mark ${i === yearIndex ? "active" : ""}`}
                      >
                        {y}
                      </span>
                    ))
                  }
                </div>
              </div>

              {/* play-button */}
              <button
                className="year-play-button"
                type="button"
                title={yearIsPlaying ? "Pause" : "Play"}
                onClick={() => setYearIsPlaying((p) => !p)}
              >
                {yearIsPlaying ? "⏸" : "▶"}
              </button>

            </div>
          </div>
          {/* topbar-timeline end */}

          {/* actions: fit zone */}
          <div className="topbar-actions">
            {/* fit zone button */}
            <button
              className="fit-zone-button"
              type="button"
              title="Fit Zone"
              onClick={fitzoneHandler}
            >
              Fit Zone
            </button>
          </div>
        </div>
      </div>

      <div className="chart-panel panel">
        <div className="chart-panel-header">
          <div className="chart-panel-title">Land Use Ratio Chart</div>
          <button
            className="chart-clear-button"
            type="button"
            onClick={clearChartHandler}
            title="Clear"
          >
            Clear
          </button>
        </div>

        {/* AreaChart */}
        <div className="chart-timeseries">
          <div className="chart-title">
            Timeseries
          </div>
          <div className="chart-timeseries-chart">
            {
              isReadyCellLanduseAreaData && areaChartData.length > 0 && areaChartKeyData.length > 0
                && (
                  <ResponsiveContainer>
                    <AreaChart
                      data={areaChartData}
                      margin={{ top: 30, bottom: 30, left: -25}}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="year"
                        width="auto"
                        axisLine={{ stroke: 'black' }}
                        tickLine={{ stroke: 'black' }}
                        tick={{ fill: 'black' }}
                        label={{
                          value: 'Year',
                          angle: 0,
                          position: 'insideBottom',
                          offset: -15,
                          style: {
                            fill: 'black',
                          },
                        }}
                      />
                      <YAxis
                        domain={[0, 1.0]}
                        ticks={[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]}
                        allowDataOverflow={true}
                        tickFormatter={(v) => `${Math.round(v * 100)}`}
                        axisLine={{ stroke: 'black' }}
                        tickLine={{ stroke: 'black' }}
                        tick={{ fill: 'black' }}
                      />
                      <RechartsLabel
                        value="(%)"
                        position="insideTop"
                        offset={-25}
                        dx={-145}
                        fill="black"
                        fontSize={16}
                      />
                      <RechartsTooltip
                        itemSorter={(item) => -areaChartKeyData.indexOf(item.dataKey)}
                        formatter={(value, name) => [
                          `${Math.round(value * 100)}%`,
                          Legend[name].label
                        ]}
                      />

                      {
                        areaChartKeyData.map((landuse, index) => {
                          const rgbColor = rgbToCss(Legend[landuse].color);

                          return (
                            <Area
                            key={`landuse-areachart-${gridType}-${selectedCell}-${landuse}`}
                              type="monotone"
                              dataKey={landuse}
                              stackId="1"
                              stroke={rgbColor}
                              fill={rgbColor}
                              isAnimationActive={false}
                            />
                          );
                        })
                      }
                    </AreaChart>
                  </ResponsiveContainer>
                )
            }
          </div>
        </div>

        <div className="chart-pie-section">
          <div className="chart-title">
            Pie for Year
          </div>

          <div className="chart-pie-control">
            {/* Year select */}
              <select
                className="chart-pie-year-select"
                value={pieYear}
                onChange={onChangePieYear}
              >
                {
                  Years.toReversed().map((year) => {
                    return (
                      <option value={year} key={`landuse-piechart-year-${year}`}>
                        {year}
                      </option>
                    );
                  })
                }
              </select>

          </div>

          {/* PieChart */}
          <div className="chart-pie-chart">
            {
              pieData.length > 0
                && (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        nameKey="landuse"
                        dataKey="area_ratio"
                        cx="50%"
                        cy="50%"
                        isAnimationActive={false}
                        paddingAngle={0}
                        startAngle={90}
                        endAngle={-270}
                        innerRadius="50%"
                        outerRadius="90%"
                      >
                        {
                          pieData.map(({landuse}, index) => {
                            const rgbColor = rgbToCss(Legend[landuse].color);

                            return (
                              <Cell key={`landuse-piechart-${gridType}-${selectedCell}-${pieYear}-${landuse}`} fill={rgbColor} />
                            );
                          })
                        }
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${Math.round(value * 100)}%`,
                          Legend[name].label
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )
            }
          </div>

        </div>
      </div>

      <div className="legend-panel panel">
        <div className="legend-title">Land Use Legend</div>

        {
          LanduseType.map((key) => {
            const item = Legend[key];

            return (
              <div className="legend-item" key={`legend-item-${key}`}>
                <div
                  className="legend-color"
                  style={{
                    backgroundColor: `rgb(${item.color.join(",")})`
                  }}
                />
                <div className="legend-label">
                  {item.label}
                </div>
              </div>
            );
          })
        }
      </div>

      <div className="map-attribution">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          ©︎ OpenStreetMap contributors
        </a>
      </div>

    </div>
  );
};

export default App;
